import type {IProdutoPreco} from "../models/IProdutoPreco.ts";
import {IRepresentante, IRepresentanteFiltersModel, IRepresentanteViewModel} from "../models/IRepresentante.ts";
import type {IClienteFilial} from "../models/IClienteFilial.ts";
import {
	IFamiliaViewModel,
	IProduto,
	IProdutoCodigoViewModel, IProdutoEstoqueViewModel,
	IProdutoFiltersModel, IProdutoLogisticaViewModel,
	IProdutoPrecoViewModel,
	IProdutoViewModel, TipoProdutoType
} from "../models/IProdutoModels.ts";
import {ICliente, IClienteFilialViewModel, IClienteViewModel, ICondPagtoClienteViewModel} from "../models/ICliente.ts";
import type {IRegra, IRegraViewModel} from "../components/regras/models/IRegra.ts";
import {type Dayjs} from "dayjs";
import type {IUser} from "../models/IUser.ts";
import {
	IAppPedido,
	IPedidoAprovacaoModel, IPedidoCaminhoesViewModel,
	IPedidoViewModel,
	IUpdatePedidoModel, TipoFreteType
} from "@components/pedidos/forcavendas/interfaces/PedidoModels.ts";
import type {IProdutoCodigo} from "../components/produtos/interfaces/IProdutoCodigo.ts";
import type {IClienteProdutoPreco} from "../components/clientes/interfaces/IClienteModels.ts";
import type {IPedidoItemViewModel} from "@components/pedidos/forcavendas/interfaces/IAppPedidoItem.ts";
import {PermissaoType} from "../models/PermissaoEnum.ts";
import {StatusType} from "@components/default/enum/StatusType.ts";
import {ITabelaPrecoFiltersModel, ITabelaPrecoViewModel} from "@components/tabelaspreco/TabelasPrecoModels.ts";
import {IUserLogViewModel} from "@components/users/IUsuarioLog.ts";
import {IProdutoRegraViewModel} from "@components/regras/models/IRegraItem.ts";
import {IPedidoItemErpViewModel} from "@components/pedidos/venda/Interfaces.ts";
import {IGrupoErpViewModel} from "@components/account/erp/AccountsErpModels.ts";

export interface IApiEndpoint<out T = any | undefined, TBody = undefined | object, TQuery = undefined | object, TProps = any | undefined> {
	url: string
	method: RequestInit['method']
	dataType: T,
	body?: TBody,
	query?: TQuery
	props: TProps
}

export interface IApiMessage {
	message: string
	severity: 'error' | 'warning' | 'info' | 'success'
	important: boolean
}

export interface IPagination {
	page: number,
	pageLimit: number,
	ignoreCache?: true
}

export interface IPaginationResponse extends IPagination {
	results?: number,
	totalPages?: number
}

export interface IApiResponse<T, TProps = any | any [] | never | undefined> {
	pagination?: IPaginationResponse,
	data?: T,
	message?: IApiMessage
	minorMessages?: IApiMessage[],
	success: boolean,
	props: TProps
}

export interface IAccountPermission {
	modulo: PermissaoType['modulo']
	tipo: PermissaoType['tipo'],
	acao?: string,
	isNegacao: boolean
}


export interface IUserPermissionViewModel {
	id: string,
	nome: string,
	modulo: PermissaoType['modulo']
	tipo: PermissaoType['tipo'],
	acao?: string,
	isNegacao: boolean
}

export interface PermissionsViewModel<T> {
	id: T,
	nome: string,
	modulo: PermissaoType['modulo'],
	tipos: {
		tipo: PermissaoType['tipo'],
		isNegacao: boolean
	}[]
}

export const produtoEndpoints = {
	produtoPreco: (id: string, tabelaId: string | undefined = undefined) => ({
		method: 'GET',
		url: tabelaId == null ? `/Api/Erp/V1/Produtos/${id}/Precos` : `/Api/Erp/V1/Produtos/${id}/Precos/${tabelaId}`
	}) as IApiEndpoint<IProdutoPreco[]>,
	produtoPrecoAtual: ({id, tabelaId}: {
		id: string, tabelaId?: string
	}) => ({
		method: 'GET',
		url: tabelaId != null ? `/Api/Erp/V1/Produtos/${id}/Precos/${tabelaId}/Atual` : `/Api/Erp/V1/Produtos/${id}/Precos/Atual`
	}) as IApiEndpoint<IProdutoPrecoViewModel[]>,
	getProduto: (id: string) => ({
		method: 'GET',
		url: `/Api/Erp/V1/Produtos/${id}`
	}) as IApiEndpoint<IProduto>,
	produtoCodigos: (id: string) => ({
		method: 'GET',
		url: `/Api/Erp/V1/Produtos/${id}/Codigos`
	}) as IApiEndpoint<IProdutoCodigo[]>,
	getProdutos: (props: {
		search?: string,
		tabelaPreco?: string,
		tipoProduto?: 'produzido' | 'string',
		isAtivo?: boolean
		familiaId?: string[]
	}) => ({
		method: 'GET',
		url: `/Api/Erp/V1/Produtos`,
		query: props
	}) as IApiEndpoint<IProduto[]>,
	produtoPrecoTabela: (produtoId: string, tabela: string) => ({
		method: 'GET',
		url: `/Api/Erp/V1/Produtos/${produtoId}/Precos/${tabela}`
	}) as IApiEndpoint<IProdutoPreco[]>,
	produtoPrecoTabelaAtual: (produtoId: string, tabela: string) => ({
		method: 'GET',
		url: `/Api/Erp/V1/Produtos/${produtoId}/Precos/${tabela}/Atual`
	}) as IApiEndpoint<IProdutoPreco>,
	erp: {
		getProdutoDadosLogistica: (produtoId: string) => ({
			method: 'GET',
			url: `/Api/Erp/V1/Produtos/${produtoId}/DadosLogistica`
		}) as IApiEndpoint<IProdutoLogisticaViewModel>,
		getProdutosDadosLogistica: (props: {
			search?: string;
			isAtivo?: boolean;
			produtoId?: string[];
			familiaId?: string[];
			codigoBarras?: string[];
			tipoProduto?: TipoProdutoType[];
		}) => ({
			method: 'GET',
			url: `/Api/Erp/V1/Produtos/DadosLogistica`,
			query: {...props}
		}) as IApiEndpoint<IProdutoLogisticaViewModel[]>,
		familias: {
			getFamilias: (props: { search?: string, tipo?: TipoProdutoType[], isAtivo?: boolean }) => ({
				method: 'GET',
				url: `/Api/Erp/V1/Produtos/Familias`,
				query: {...props}
			}) as IApiEndpoint<IFamiliaViewModel[]>
		},
		estoque: {
			getEstoqueItem: (props: {
				produtoId: string,
				query?: {
					depositoId?: string,
					orderBy?: 'produtoId' | 'produtoNome' | 'deposito' | 'qtdEstoque'
				}
			}) => {
				return ({
					method: 'GET',
					url: `/Api/Erp/V1/Produtos/${props.produtoId}/Estoques`,
					query: {...props.query}
				}) as IApiEndpoint<IProdutoEstoqueViewModel>
			}
		}
	},
	app: {
		getCodigos: (props: IProdutoFiltersModel) => ({
			url: `/Api/App/V1/Produtos/Codigos`,
			method: 'GET',
			query: {...props}
		}) as IApiEndpoint<IProdutoCodigoViewModel[]>,
		getProdutos: (props?: IProdutoFiltersModel) => ({
			url: `/Api/App/V1/Produtos`,
			method: 'GET',
			query: {...props}
		}) as IApiEndpoint<IProdutoViewModel[]>,
		getProduto: (id: string) => ({
			url: `/Api/App/V1/Produtos/${id}`,
			method: 'GET',
		}) as IApiEndpoint<IProdutoViewModel>,
		getCodigosProduto: (id: string) => ({
			url: `/Api/App/V1/Produtos/${id}/Codigos`,
			method: 'GET',
		}) as IApiEndpoint<IProdutoCodigoViewModel[]>,
		getPrecoAtual: (props: { produtoId: string, tabelaId?: string }) => ({
			url: props.tabelaId == null ? `/Api/App/V1/Produtos/${props.produtoId}/Precos/Atual` : `/Api/App/V1/Produtos/${props.produtoId}/Precos/${props.tabelaId}/Atual`,
			method: 'GET',
		}) as IApiEndpoint<IProdutoPrecoViewModel[]>,
		getPrecosDisponiveisMeusClientes: () => ({
			url: 'Api/App/V1/Produtos/Precos/Clientes/Meus/Disponiveis',
			method: 'GET',
		}) as IApiEndpoint<IProdutoPrecoViewModel[]>,
		getFamilias: (props: {
			search?: string,
			tipoFamilia?: IFamiliaViewModel['tipoFamilia']
		}) => ({
			method: 'GET',
			url: `/Api/App/V1/Produtos/Familias`,
			query: {...props}
		}) as IApiEndpoint<IFamiliaViewModel[]>,
		getColecoes: (props: { search?: string }) => ({
			method: 'GET',
			url: `/Api/App/V1/Produtos/Colecoes`,
			query: {...props}
		}) as IApiEndpoint<{ id: string, nome: string }>

	}

} as const;

export const representanteEndpoints = {
	representante: (id: number) => ({
		method: 'GET',
		url: `/Api/Erp/V1/Representantes/${id}`
	}) as IApiEndpoint<IRepresentante>,
	clientesRepresentante: (id: number) => ({
		method: 'GET',
		url: `/Api/Erp/V1/Representantes/${id}/Clientes`
	}) as IApiEndpoint<IClienteFilial[]>,
	representantes: (props: { search?: string, ativos?: boolean }) => ({
		method: 'GET',
		url: `/Api/Erp/v1/Representantes`,
		query: {...props}
	}) as IApiEndpoint<IRepresentante[]>,
	app: {
		getRepresentantesDetalhes: (filters?: IRepresentanteFiltersModel) => ({
			method: 'GET',
			url: `/Api/App/V1/Representantes/Detalhes`,
			query: filters
		}) as IApiEndpoint<IRepresentanteViewModel[]>,
		getRepresentanteDetalhes: (id: number) => ({
			method: 'GET',
			url: `/Api/App/V1/Representantes/${id}/Detalhes`
		}) as IApiEndpoint<IRepresentanteViewModel>,
		vincularRepresentante: (props: { representanteId: number, userId: string }) => ({
			method: 'PATCH',
			url: `/Api/App/V1/Representantes/${props.representanteId}/Usuarios/${props.userId}`,
			body: props
		}) as IApiEndpoint<IRepresentanteViewModel[]>,
		desvincularRepresentante: (props: { representanteId: number, userId: string }) => ({
			method: 'DELETE',
			url: `/Api/App/V1/Representantes/${props.representanteId}/Usuarios/${props.userId}`,
			body: props
		}) as IApiEndpoint<IRepresentanteViewModel[]>,
		getMeusRepresentantes: (props: IRepresentanteFiltersModel) => ({
			method: 'GET',
			url: `/Api/App/V1/Representantes/MeusRepresentantes`,
			query: props
		}) as IApiEndpoint<IRepresentanteViewModel[]>,
		updateRepresentanteLimite: (props: { representanteId: number, limite: number }) => ({
			method: 'PATCH',
			url: `/Api/App/V1/Representantes/${props.representanteId}/Limite`,
			query: {limite: props.limite},
		}) as IApiEndpoint<null>
	}
} as const

export interface IClienteFiltersModel {
	id?: number[],
	search?: string,
	cpfCnpj?: string,
	isAtivo?: boolean,
	representanteId?: number[],
	empresaId?: number[],
	filialId?: number,
	tabelasPrecoId?: string[]
}

export const clienteEndpoints = {
	clientes: () => ({
		method: 'GET',
		url: `/Api/Erp/V1/Clientes`,
	}) as IApiEndpoint<ICliente[]>,
	clientesTabelas: (clienteId: number) => ({
		method: 'GET',
		url: `/Api/Erp/V1/Clientes/${clienteId}/TabelaPrecos`
	}) as IApiEndpoint<IClienteFilial[]>,
	clienteProdutoPreco: (clienteId: number, produtoId: string) => ({
		method: 'GET',
		url: `/Api/Erp/V1/Clientes/${clienteId}/Produtos/${produtoId}/Precos`
	}) as IApiEndpoint<IClienteProdutoPreco>,
	app: {
		getCliente: (id: number) => ({
			method: 'GET',
			url: `/Api/App/V1/Clientes/${id}`
		}) as IApiEndpoint<IClienteViewModel>,
		getClientes: (props: IClienteFiltersModel) => ({
			method: 'GET',
			url: `/Api/App/V1/Clientes`,
			query: props
		}) as IApiEndpoint<IClienteViewModel[]>,
		getMeusClientes: (props: IClienteFiltersModel) => ({
			method: 'GET',
			url: `/Api/App/V1/Clientes/MeusClientes`,
			query: props
		}) as IApiEndpoint<IClienteFilialViewModel[]>,
		getClienteFiliais: (id: number) => ({
			method: 'GET',
			url: `/Api/App/V1/Clientes/${id}/Filiais`
		}) as IApiEndpoint<IClienteFilialViewModel[]>,
		getClienteFormasPagto: (id: number) => ({
			url: `/Api/App/V1/Clientes/${id}/FormaCondPagto`,
			method: 'GET',
		}) as IApiEndpoint<ICondPagtoClienteViewModel[]>,
		getFormaPagto: (formaPagtoId: string) => ({
			url: `/Api/App/V1/Clientes/FormaCondPagto/${formaPagtoId}`,
			method: 'GET',
		}) as IApiEndpoint<ICondPagtoClienteViewModel>
	}
}

export type ICreatePedidoDto = {
	empresaId: number,
	filialId: number,
	descricao: string
	clienteId?: number
	clienteCnpj?: string,
	dataEntrega?: Dayjs | null,
	representanteId?: number,
	tipoFrete?: TipoFreteType,
	ordemCompraClienteId: string
	condPagtoId?: string
}

export type addItemPedidoDto = {
	produtoId: string,
	pedidoId: number,
	body: {
		codigoTipo: string,
		codigoBarras?: string,
		qtd: number,
		valor: number,
		valorBonificacao?: number,
		qtdBonificar?: number
	}
}

export interface IPedidoFiltersModel {
	id?: number[];
	idErp?: number[];
	idErpBonificacao?: number[];
	search?: string;
	idFilial?: number[];
	idProdutos?: string[];
	vlrMin?: number;
	vlrMax?: number;
	vlrBonifMin?: number;
	vlrBonifMax?: number;
	qtdItensMin?: number;
	qtdItensMax?: number;
	qtdUnidadesMin?: number;
	qtdUnidadesMax?: number;
	tabelaPrecoId?: string[];
	status?: StatusType[];
	tipoFrete?: string[];
	uf?: string[];
	cidade?: string[];
	idRepresentante?: number[];
	cpfCnpj?: string[];
	dataPedidoMin?: Dayjs
	dataPedidoMax?: Dayjs
	dataGeracaoMin?: Dayjs
	dataGeracaoMax?: Dayjs
}

export const pedidoEndpoints = {
	erp: {
		getPedido: (id: number) => ({
			method: 'GET',
			url: `/Api/Erp/V1/Pedidos/${id}`
		}) as IApiEndpoint<any>,
		getPedidoItens: (id: number) => ({
			method: 'GET',
			url: `/Api/Erp/V1/Pedidos/${id}/Itens`
		}) as IApiEndpoint<IPedidoItemErpViewModel[]>,
		getPedidoByForcaVendas: (id: number) => ({
			method: 'GET',
			url: `/Api/Erp/V1/Pedidos/ForcaDeVendas/${id}/Itens`
		}) as IApiEndpoint<IPedidoItemErpViewModel[]>
	},
	app: {
		getPedidos: (filters: IPedidoFiltersModel) => ({
			method: 'GET',
			url: `/Api/App/V1/Pedidos`,
			query: filters
		}) as IApiEndpoint<IAppPedido[]>,
		getPedidosDetalhes: (filters: IPedidoFiltersModel) => ({
			method: 'GET',
			url: `/Api/App/V1/Pedidos/Detalhes`,
			query: filters
		}) as IApiEndpoint<IPedidoViewModel[]>,
		getPedido: (id: number) => ({
			method: 'GET',
			url: `/Api/App/V1/Pedidos/${id}`
		}) as IApiEndpoint<IAppPedido>,
		getPedidosMeus: (filters: IPedidoFiltersModel) => ({
			method: "GET",
			query: filters,
			url: `/Api/App/V1/Pedidos/MeusPedidos`
		}) as IApiEndpoint<IPedidoViewModel[]>,
		getPedidoDetalhes: (id: number) => ({
			method: 'GET',
			url: `/Api/App/V1/Pedidos/${id}/Detalhes`
		}) as IApiEndpoint<IPedidoViewModel>,
		getPedidoItens: (id: number) => ({
			method: 'GET',
			url: `/Api/App/V1/Pedidos/${id}/Itens`
		}) as IApiEndpoint<IPedidoItemViewModel[]>,
		createPedido: (props: ICreatePedidoDto) => ({
			method: 'POST',
			url: `/Api/App/V1/Pedidos`,
			body: props
		}) as IApiEndpoint<IAppPedido, ICreatePedidoDto>,
		updatePedido: (props: { id: number, body: Partial<IUpdatePedidoModel> }) => ({
			method: 'PATCH',
			url: `/Api/App/V1/Pedidos/${props.id}`,
			body: props.body
		}) as IApiEndpoint<IAppPedido>,
		addPedidoItem: (data: addItemPedidoDto) => ({
			method: 'POST',
			url: `/Api/App/V1/Pedidos/${data.pedidoId}/Itens/Produtos/${data.produtoId}`,
			body: data.body
		}) as IApiEndpoint<any, addItemPedidoDto['body']>,
		aprovarPedido: (id: number) => ({
			method: 'PATCH',
			url: `/Api/App/V1/Pedidos/${id}/Aprovacao`
		}) as IApiEndpoint<any>,
		reprovarPedido: (id: number) => ({
			method: 'DELETE',
			url: `/Api/App/V1/Pedidos/${id}/Aprovacao`
		}) as IApiEndpoint<any>,
		fecharPedido: (id: number) => ({
			method: 'POST',
			url: `/Api/App/V1/Pedidos/${id}/Fechar`
		}) as IApiEndpoint<any>,
		cancelarPedido: (id: number) => ({
			method: 'DELETE',
			url: `/Api/App/V1/Pedidos/${id}`
		}) as IApiEndpoint<any>,
		cancelarItemPedido: (props: { pedidoId: number, produtoId: string }) => ({
			method: 'DELETE',
			url: `/Api/App/V1/Pedidos/${props.pedidoId}/Itens/${props.produtoId}`
		}) as IApiEndpoint<any>,
		gerarPedido: (id: number) => ({
			method: 'POST',
			url: `/Api/App/V1/Pedidos/${id}/GerarPedidoErp`
		}) as IApiEndpoint<any>,
		aprovarItemPedido: (props: {
			pedidoId: number,
			mode: 'aprovar' | 'reprovar'
			body: {
				items: { produtoId: string, codigoProduto: number }[]
			}
		}) => ({
			url: `/Api/App/V1/Pedidos/${props.pedidoId}/Itens/Aprovacao`,
			method: props.mode == 'aprovar' ? 'PATCH' : 'DELETE',
			body: props.body
		}) as IApiEndpoint<any>,
		getPedidoAprovacao: (props: { id: number }) => ({
			method: 'GET',
			url: `/Api/App/V1/Pedidos/${props.id}/Aprovacao`
		}) as IApiEndpoint<IPedidoAprovacaoModel[]>,
		updatePedidoItem: ({pedidoId, produtoId, body}: {
			pedidoId: number, produtoId: string, body: {
				qtd: number,
				codigoTipo: string,
				codigoBarras?: string,
				valor: number,
				qtdBonificar?: number
			}
		}) => ({
			method: 'PATCH',
			url: `/Api/App/V1/Pedidos/${pedidoId}/Itens/Produtos/${produtoId}`,
			body: {...body}
		}) as IApiEndpoint<any>,
		getSeparacaoCaminhoes: (props: { pedidoId: number }) => ({
			method: 'GET',
			url: `/Api/App/V1/Pedidos/${props.pedidoId}/Caminhoes`
		}) as IApiEndpoint<IPedidoCaminhoesViewModel>,
		graphs: {
			pedidosGraph: (props: {
				mode: 'cliente' | 'representante' | 'uf' | 'familia' | 'produto'
				filters?: IPedidoFiltersModel
			}) => ({
				method: 'GET',
				url: `/Api/App/V1/Pedidos/Graphs/${props.mode}`,
				query: props.filters
			}) as IApiEndpoint<{
				key: number,
				label: string,
				mode: "cliente" | 'representante' | 'uf',
				valoresMonetarios: Record<string, number>
				valoresQuantidades: Record<string, number>
			}[]>
		}
	}
} as const


export const tabelaPrecoEndpoints = {
	getTabelaPreco: (tabelaId: string) => ({
		method: 'GET',
		url: `/Api/Erp/V1/TabelaPrecos/${tabelaId}`
	}) as IApiEndpoint<any>,
	getProdutosTabelaPreco: (tabelaId: string) => ({
		method: 'GET',
		url: `/Api/Erp/V1/TabelaPrecos/${tabelaId}/Produtos`
	}) as IApiEndpoint<IProduto[]>,
	app: {
		getTabelas: (props?: ITabelaPrecoFiltersModel) => ({
			method: 'GET',
			url: `/Api/App/V1/TabelaPrecos`,
			query: props
		}) as IApiEndpoint<ITabelaPrecoViewModel[]>,
		getTabela(id: string) {
			return {
				method: 'GET',
				url: `/Api/App/V1/TabelaPrecos/${id}`
			} as IApiEndpoint<ITabelaPrecoViewModel>
		},
	}
}
type createRegraDto = {
	nome: string,
	descricao?: string,
	descontoPercMax?: number,
	descontoBrutoMax?: number,
	acao?: 'aprova' | 'rejeita',
	isRegraPadrao?: boolean,
	qtdLimite?: number,
	dataInicial: Dayjs,
	dataFinal?: Dayjs
}

export type addItemRegraDto = {
	regraId: number,
	body: {
		id: string,
		tipo: 'produto' | 'familia',
		descontoMax?: number,
		qtdLimite?: number
	}
}

export const regrasEndpoints = {
	createRegra: (props: createRegraDto) => ({
		method: 'POST',
		url: `/Api/App/V1/Regras`,
		body: props
	}) as IApiEndpoint<IRegra, createRegraDto>,
	addItemRegra: (props: addItemRegraDto) => ({
		method: 'POST',
		url: `/Api/App/V1/Regras/${props.regraId}/Itens`,
		body: props.body
	}) as IApiEndpoint<IRegra, addItemRegraDto['body']>,
	getRegras: (query?: { search?: string }) => ({
		method: 'GET',
		url: `/Api/App/V1/Regras`,
		query
	}) as IApiEndpoint<IRegraViewModel[]>,
	getRegra: (id: number) => ({
		method: 'GET',
		url: `/Api/App/V1/Regras/${id}`
	}) as IApiEndpoint<IRegra>,
	getRegraItens: (id: number) => ({
		method: 'GET',
		url: `/Api/App/V1/Regras/${id}/Itens`
	}) as IApiEndpoint<IProdutoRegraViewModel[]>
}

export const accountEndpoints = {
	login: (props: { username: string, password: string }) => ({
		method: 'POST',
		url: `/Api/App/V1/Account/Login`,
		body: props
	}) as IApiEndpoint<{ user: IUser, token: string }>,
	getPermissions: () => ({
		method: 'GET',
		url: `/Api/App/V1/Account/Permissions`
	}) as IApiEndpoint<IUserPermissionViewModel[]>,
	changePwd: (props: { oldPassword: string, newPassword: string }) => ({
		method: 'POST',
		url: `/Api/App/V1/Account/ChangePassword`,
		body: props
	}) as IApiEndpoint<IUser>
}

// Users & Roles (from OpenAPI /api/app/v1)
export interface IUserViewModel {
	id: string
	userName: string
	nomeCompleto: string
	email: string
	isAtivo: boolean
	dataCriacao: string
	ultimoLogin?: string | null
	roles?: { id: number, nome: string }[]
}

export interface IUserMinimalViewModel extends Pick<IUserViewModel, 'id' | 'userName' | 'isAtivo'> {
	nome: string
	nomeCompleto?: string
}

export type CreateUserModel = {
	username: string
	nomeCompleto: string
	email: string
}

export type UpdateUserModel = {
	nomeCompleto?: string
	email?: string
	isAtivo?: boolean | null
}

export interface IRoleViewModel {
	id: number
	nome: string
	descricao?: string | null
	isAtivo: boolean
	dataCriacao: string
}

/**
 * Representa a associação entre uma Role e uma Permissão.
 * @property {isAtivo} {boolean} indica se a associação entre a Role e a Permissão está ativa ou não, caso ela simplesmente não exista o valor é false.
 */
export interface IRolePermissionModel extends PermissaoType {
	roleId: number,
	empresaId?: number,
	filialId?: number,
	isNegacao: boolean,
	isAtivo: boolean,
	dataCriacao?: Date
}

export type CreateRoleModel = {
	nome: string
	descricao?: string | null
}

export type UpdateRoleModel = {
	nome?: string
	descricao?: string | null
	isAtivo?: boolean | null
}

export type UserRoleModel = {
	usuarioId: string
	roleId: number
}

export type UserRoleViewModel = {
	roleId: number,
	roleNome: string,
	roleAtiva: boolean,
	usuarioId: string,
	usuarioNome: string,
	usuarioLogin: string,
	usuarioEmail?: string,
	dataVinculo: string,
	usuarioAtivo: boolean
}

export const usersEndpoints = {
	getUsers: (query?: { search?: string }) => ({
		method: 'GET',
		url: `/Api/App/V1/Users`,
		query
	}) as IApiEndpoint<IUserViewModel[]>,
	getUser: (id: string) => ({
		method: 'GET',
		url: `/Api/App/V1/Users/${id}`
	}) as IApiEndpoint<IUserViewModel>,
	createUser: (body: CreateUserModel) => ({
		method: 'POST',
		url: `/Api/App/V1/Users`,
		body
	}) as IApiEndpoint<IUserViewModel, CreateUserModel>,
	updateUser: (id: string, body: UpdateUserModel) => ({
		method: 'PUT',
		url: `/Api/App/V1/Users/${id}`,
		body
	}) as IApiEndpoint<IUserViewModel, UpdateUserModel>,
	toggleStatus: (id: string) => ({
		method: 'PATCH',
		url: `/Api/App/V1/Users/${id}/ToggleStatus`
	}) as IApiEndpoint<IUserViewModel>,
	getUserLogs: (id: string) => ({
		method: 'GET',
		url: `/Api/App/V1/Users/${id}/Logs`
	}) as IApiEndpoint<IUserLogViewModel[]>,
	changeUserPwd: (id: string, body: { newPassword: string }) => ({
		method: 'PUT',
		url: `/Api/App/V1/Users/${id}/Password`,
		body
	}) as IApiEndpoint<IUserViewModel>,
	v2: {
		getPermissions: (props: { id: string }) => ({
			method: 'GET',
			url: `/Api/App/V1/Users/${props.id}/Permissions`
		}) as IApiEndpoint<PermissionsViewModel<string>[]>
	}
} as const
export const rolesEndpoints = {
	preffix: `/Api/App`,
	getRoles: (query?: { search?: string }) => ({
		method: 'GET',
		url: `${rolesEndpoints.preffix}/V1/Roles`,
		query
	}) as IApiEndpoint<IRoleViewModel[]>,
	getRole: (id: number) => ({
		method: 'GET',
		url: `${rolesEndpoints.preffix}/V1/Roles/${id}`
	}) as IApiEndpoint<IRoleViewModel>,
	createRole: (body: CreateRoleModel) => ({
		method: 'POST',
		url: `${rolesEndpoints.preffix}/V1/Roles`,
		body
	}) as IApiEndpoint<IRoleViewModel, CreateRoleModel>,
	updateRole: (id: number, body: UpdateRoleModel) => ({
		method: 'PUT',
		url: `${rolesEndpoints.preffix}/V1/Roles/${id}`,
		body
	}) as IApiEndpoint<IRoleViewModel, UpdateRoleModel>,
	toggleStatus: (id: number) => ({
		method: 'PATCH',
		url: `${rolesEndpoints.preffix}/V1/Roles/${id}/ToggleStatus`
	}) as IApiEndpoint<IRoleViewModel>,
	getAllPermissions: () => ({
		method: 'GET',
		url: `${rolesEndpoints.preffix}/V1/Roles/Permissions/All`
	}) as IApiEndpoint<IRolePermissionModel[]>,
	getRolePermissions: (roleId: number) => ({
		method: 'GET',
		url: `${rolesEndpoints.preffix}/V1/Roles/${roleId}/Permissions`
	}) as IApiEndpoint<IRolePermissionModel[]>,
	addRolePermission: (roleId: number, permissao: PermissaoType, isNegacao: boolean) => ({
		method: 'POST',
		url: `${rolesEndpoints.preffix}/V1/Roles/${roleId}/Permissions/${permissao.modulo}/${permissao.tipo}`,
		body: {isNegacao, acao: permissao.acao ?? ""}
	}) as IApiEndpoint<any, { acao: string, isNegacao: boolean }>,
	removeRolePermission: (roleId: number, permissao: PermissaoType) => ({
		method: 'DELETE',
		url: `${rolesEndpoints.preffix}/V1/Roles/${roleId}/Permissions/${permissao.modulo}/${permissao.tipo}?acao=${encodeURIComponent(permissao.acao ?? '')}`,
	}) as IApiEndpoint<any>,
	assignUserRole: (data: UserRoleModel) => ({
		method: 'POST',
		url: `${rolesEndpoints.preffix}/V1/Roles/${data.roleId}/Users/${data.usuarioId}`,
		body: data
	}) as IApiEndpoint<any, UserRoleModel>,
	removeUserRole: (data: UserRoleModel) => ({
		method: 'DELETE',
		url: `${rolesEndpoints.preffix}/V1/Roles/${data.roleId}/Users/${data.usuarioId}`,
		body: data
	}) as IApiEndpoint<any, UserRoleModel>,
	getRoleUsers: (roleId: number) => ({
		method: 'GET',
		url: `${rolesEndpoints.preffix}/V1/Roles/${roleId}/Users`
	}) as IApiEndpoint<UserRoleViewModel[]>,
	v2: {
		getPermissions: (roleId: number) => ({
			url: `${rolesEndpoints.preffix}/V2/Roles/${roleId}/Permissions`,
			method: 'GET'
		}) as IApiEndpoint<PermissionsViewModel<number>>,
		getGruposVinculados: (roleId: number) => ({
			url: `${rolesEndpoints.preffix}/V2/Roles/${roleId}/GruposErp`,
			method: 'GET'
		}) as IApiEndpoint<(IGrupoErpViewModel & { dataCriacao: string })[]>,
		assignGrupo: (roleId: number, grupoId: number) => ({
			method: 'POST',
			url: `${rolesEndpoints.preffix}/V2/Roles/${roleId}/GruposErp/${grupoId}`,
		}) as IApiEndpoint<any>,
		removeGrupo: (roleId: number, grupoId: number) => ({
			url: `${rolesEndpoints.preffix}/V2/Roles/${roleId}/GruposErp/${grupoId}`,
			method: 'DELETE'
		}) as IApiEndpoint<any>,
	}
} as const;

export interface ISavedFilterViewModel {
	id: number
	nome: string
	key: string
	jsonData: string
	criadoPorId: string
	criadoPorNome: string
	isSharedWithAll: boolean
	isOwner: boolean
	sharedWithRoleIds: number[]
	sharedWithUserIds: string[]
}

export type ISaveFilterModel = {
	id?: number
	nome: string
	key: string
	jsonData: string
	isSharedWithAll: boolean
	sharedWithRoleIds?: number[]
	sharedWithUserIds?: string[]
}

export const savedFiltersEndpoints = {
	getFilters: (key: string) => ({
		method: 'GET',
		url: `/Api/App/V1/SavedFilters/${encodeURIComponent(key)}`
	}) as IApiEndpoint<ISavedFilterViewModel[]>,
	saveFilter: (body: ISaveFilterModel) => ({
		method: 'POST',
		url: `/Api/App/V1/SavedFilters`,
		body
	}) as IApiEndpoint<{ id: number }, ISaveFilterModel>,
	deleteFilter: (id: number) => ({
		method: 'DELETE',
		url: `/Api/App/V1/SavedFilters/${id}`
	}) as IApiEndpoint<void>
} as const;

export const geralEndpoints = {
	version: () => ({
		method: 'GET',
		url: '/Api/App/V1/Geral/Version'
	}) as IApiEndpoint<{ version: string }>
} as const;
